import {
  CALENDAR_EVENT_DISPLAY_MODES,
  calendarEventEndDateKey,
  calendarEventEndTime,
  calendarEventSourceTimezone,
  calendarEventStartTime,
} from '@/js/core/utils/calendar-event-time';
import { formatDateKey, isDateBeforeToday, parseDateTime } from '@/js/core/utils/formatters';
import { DEFAULT_TIMEZONE } from '@/js/core/utils/timezone';

/**
 * How closely an event's end is judged.
 *
 * `day` retires an event once its last date is behind us, so it stays up for the whole of its
 * final day. `time` retires a timed event the moment its end time passes.
 */
export const EVENT_EXPIRY_PRECISION = {
  day: 'day',
  time: 'time',
};

/**
 * Whether an event has finished and should leave the view it is in.
 *
 * Only the `time` precision reads a clock: an event with a real end time is judged on two absolute
 * instants, so it expires at the same moment whatever timezone it is being viewed in. Everything
 * else — date-only events, and timed events under `day` precision — is judged on date keys, which
 * resolve in the viewer's timezone.
 *
 * @param {object} event
 * @param {{ timezone?: string, now?: number, precision?: string }} options
 *   `now` is epoch milliseconds and applies to `time` precision only; the `day` branch always
 *   reads the current date.
 * @returns {boolean}
 */
export function isEventExpired(
  event,
  { timezone = DEFAULT_TIMEZONE, now = Date.now(), precision = EVENT_EXPIRY_PRECISION.day } = {}
) {
  return precision === EVENT_EXPIRY_PRECISION.time
    ? hasEventEndTimePassed(event, timezone, now)
    : hasEventEndDatePassed(event, timezone);
}

/**
 * Time precision: a timed event goes the moment it ends.
 *
 * @param {object} event
 * @param {string} timezone
 * @param {number} now  Epoch milliseconds.
 * @returns {boolean}
 */
export function isDisplayEventExpired(event, timezone = DEFAULT_TIMEZONE, now = Date.now()) {
  return isEventExpired(event, { timezone, now, precision: EVENT_EXPIRY_PRECISION.time });
}

function hasEventEndDatePassed(event, timezone) {
  if (event?.display_mode === CALENDAR_EVENT_DISPLAY_MODES.dateOnly) {
    const dateKey = calendarEventEndDateKey(event, timezone);
    const todayKey = formatDateKey(new Date(), timezone);

    return Boolean(dateKey && todayKey && dateKey < todayKey);
  }

  // Use the end time so an in-progress multi-day event is not treated as past until every one of its days has elapsed.
  return isDateBeforeToday(
    calendarEventEndTime(event),
    timezone,
    calendarEventSourceTimezone(event)
  );
}

/**
 * Only a timed event has a real end. The other two modes get their whole day, judged on the date
 * key: the projection stamps date_only rows `23:59:59` in UTC, which is early evening in Eastern,
 * so reading that as a clock time would retire them hours early.
 */
function hasEventEndTimePassed(event, timezone, now) {
  const endTimestamp = eventEndTimestamp(event);

  if (endTimestamp !== null) {
    return endTimestamp <= now;
  }

  const endKey = event?.range_end_key || calendarEventEndDateKey(event, timezone);

  return Boolean(endKey) && endKey < formatDateKey(now, timezone);
}

/**
 * When a timed event actually stops, or null to fall back to the whole-day rule.
 * calendarEventEndTime falls back to the start when no end is set, which would retire an event
 * the instant it began.
 */
function eventEndTimestamp(event) {
  if (event?.display_mode !== CALENDAR_EVENT_DISPLAY_MODES.timed) {
    return null;
  }

  const endTime = calendarEventEndTime(event);

  if (!endTime || endTime === calendarEventStartTime(event)) {
    return null;
  }

  return parseDateTime(endTime, calendarEventSourceTimezone(event))?.getTime() ?? null;
}
